import React, { useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../../firebase'
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore'
import Icon from '../../../components/AppIcon'
import Button from '../../../components/ui/Button'
import OperatorPinDialog from '../../../components/auth/OperatorPinDialog'
import ReportManagerPinDialog from './ReportManagerPinDialog'
import { fetchGoldTransactions, holdProgress, isAwaitingReport, isEnvelopeReady, daysUntilReady, isCancelledTx, toDate, usd, grams, HOLD_DAYS } from '../lib/goldTx'
import { logGoldEvent } from '../lib/goldAudit'
import { reportEnvelopeToRiss, claimReportedEnvelope, envelopeActionError } from '../lib/envelopeActions'

const fmtDate = (d) =>
  d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

// The purchase contract is separate from reporting to the receiving authority.
function ContractMenu({ tx }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative flex-shrink-0" onKeyDown={(event) => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation()
        setOpen(false)
        event.currentTarget.querySelector('button')?.focus()
      }
    }}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        iconName="FileText"
        iconPosition="left"
        className="min-h-11"
        aria-expanded={open}
        aria-label={`Contract actions for envelope ${tx.envelopeNumber || tx.transactionNumber}`}
      >
        Contract
        <Icon name="ChevronDown" size={14} className="ml-1.5" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-[70] w-56 bg-card border border-border rounded-xl shadow-elevation-3 p-1.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                window.open(`/exchange-desk/contract/${tx.id}?print=1`, '_blank', 'noopener,noreferrer')
              }}
              className="min-h-11 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted/50 text-left"
            >
              <Icon name="Download" size={16} className="text-primary" />
              Print / save contract PDF
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const deliveryLocked = tx => Boolean(tx?.reportDelivery && tx.reportDelivery.state !== 'uploaded' &&
  !(tx.reportDelivery.state === 'failed' && tx.reportDelivery.retryable === true))
const canReport = tx => isAwaitingReport(tx) && (!tx.reportDelivery ||
  (tx.reportDelivery.state === 'failed' && tx.reportDelivery.retryable === true))

function LegacyPoliceReportDetails({ tx, className = '' }) {
  if (tx.reportStatus !== 'legacy_reported') return null
  return (
    <div className={`text-xs text-muted-foreground break-words ${className}`}>
      <p className="font-medium text-foreground">Local police · reported by email</p>
      <p className="mt-1">Historical report confirmed by owner; not sent through RISS.</p>
      <p className="mt-1">{HOLD_DAYS}-day hold counted from purchase date · {fmtDate(tx.holdStartedAt)}.</p>
    </div>
  )
}

function EnvelopeRow({ tx, now, onClaim, claiming, onReport, reporting, reportBlocked, reportError, actionsDisabled, onCancelEnvelope, canClaimEnvelopes = true }) {
  const reportErrorRef = useRef(null)
  const reportErrorId = `envelope-report-error-${tx.id}`
  const awaitingReport = isAwaitingReport(tx)
  const ready = !awaitingReport && isEnvelopeReady(tx, now)
  const progress = awaitingReport ? null : holdProgress(tx, now)
  const daysLeft = awaitingReport ? null : daysUntilReady(tx, now)
  const sending = reporting === tx.id || tx.reportDelivery?.state === 'sending'
  const needsReview = reportBlocked || tx.reportDelivery?.state === 'needs_review'
  const reportDisabled = Boolean(actionsDisabled || sending || needsReview || !canReport(tx))

  useEffect(() => {
    const alert = reportErrorRef.current
    if (!reportError || !alert) return
    // Keep the failure beside the affected purchase, including long lists.
    // Focus must not itself jump the page when the message is already visible.
    alert.focus({ preventScroll: true })
    const bounds = alert.getBoundingClientRect()
    const viewport = window.visualViewport
    const top = viewport?.offsetTop ?? 0
    const left = viewport?.offsetLeft ?? 0
    const bottom = top + (viewport?.height ?? window.innerHeight)
    const right = left + (viewport?.width ?? window.innerWidth)
    if (bounds.top < top + 8 || bounds.bottom > bottom - 8 || bounds.left < left || bounds.right > right) {
      alert.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' })
    }
  }, [reportError])

  return (
    <article aria-label={`Envelope ${tx.envelopeNumber || tx.transactionNumber}`} className={`rounded-xl border p-4 ${ready ? 'border-success/40 bg-success/5' : 'border-border bg-card'}`}>
      {/* Contract printing remains separate from reporting. */}
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${ready ? 'bg-success/15 text-success' : 'bg-primary/10 text-primary'}`}>
          <Icon name={ready ? 'PackageCheck' : 'Package'} size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground truncate">
            Envelope {tx.envelopeNumber || '—'}
            <span className="text-muted-foreground font-normal"> · {tx.transactionNumber}</span>
          </p>
          <p className="text-sm text-muted-foreground truncate">
            {tx.customerName || 'Customer'} · {fmtDate(tx.createdAt)} · {grams(tx.weightGrams)} · {usd(tx.payout)}
          </p>
        </div>
        <ContractMenu tx={tx} />
      </div>

      {/* A pending production report has no running hold or timer. */}
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          {awaitingReport ? (
            <p className="text-sm leading-6 text-muted-foreground">
              {sending ? 'Sending report to RISS. The hold starts only after the server verifies delivery.'
                : needsReview ? 'Report delivery needs review. Refresh or contact an administrator before sending again; the hold has not started.'
                  : tx.reportDelivery?.state === 'failed' ? tx.reportDelivery.retryable === true
                    ? 'Report was not sent. You can retry explicitly; the hold has not started.'
                    : 'Report could not be sent. Contact an administrator before retrying; the hold has not started.'
                    : `${HOLD_DAYS}-day hold not started. Send the report to RISS to begin.`}
            </p>
          ) : (
            <>
              <div className="h-2 bg-muted/60 rounded-full overflow-hidden" role="progressbar" aria-label={`${HOLD_DAYS}-day hold progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
                <div
                  className={`h-full rounded-full transition-all ${ready ? 'bg-success' : 'bg-primary/60'}`}
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <p className={`text-xs mt-2 ${ready ? 'text-success font-medium' : 'text-muted-foreground'}`}>
                {ready
                  ? `Ready — ${HOLD_DAYS}-day hold complete`
                  : daysLeft == null ? `Hold confirmed · ready ${fmtDate(tx.holdUntil)}`
                    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left · ready ${fmtDate(tx.holdUntil)}`}
              </p>
              {tx.reportStatus === 'legacy_reported' ? (
                <LegacyPoliceReportDetails tx={tx} className="mt-2" />
              ) : (
                <p className="mt-1 text-xs text-muted-foreground break-words">
                  {tx.reportConfirmation?.method === 'cloud_delivery' ? 'Report sent' : 'Report confirmed'} {fmtDate(tx.reportedAt)}{tx.reportReference ? ` · ${tx.reportReference}` : ''}
                </p>
              )}
            </>
          )}
        </div>

        {awaitingReport && (
          <Button onClick={() => onReport(tx)} disabled={reportDisabled} loading={reporting === tx.id} aria-describedby={reportError ? reportErrorId : undefined} iconName="Send" iconPosition="left" className="min-h-11 w-full sm:w-auto flex-shrink-0">
            Report to start countdown
          </Button>
        )}

        {ready && canClaimEnvelopes && (
          <Button
            variant="success"
            onClick={() => onClaim(tx)}
            loading={claiming === tx.id}
            disabled={Boolean(claiming || actionsDisabled || deliveryLocked(tx) || reportBlocked)}
            iconName="Check"
            iconPosition="left"
            className="min-h-11 w-full sm:w-auto flex-shrink-0"
          >
            Claim this envelope
          </Button>
        )}
        {ready && !canClaimEnvelopes && (
          <p className="text-xs text-muted-foreground">Administrator access required to claim.</p>
        )}
      </div>
      {reportError && (
        <div ref={reportErrorRef} id={reportErrorId} role="alert" tabIndex={-1} className="mt-3 scroll-mt-24 scroll-mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive break-words focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 focus:ring-offset-card">
          {reportError.message}
        </div>
      )}
      {onCancelEnvelope && (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => onCancelEnvelope(tx)} disabled={Boolean(actionsDisabled || sending || needsReview || deliveryLocked(tx) || claiming === tx.id)} iconName="Ban" iconPosition="left" className="min-h-11 text-destructive hover:text-destructive">
            Cancel envelope
          </Button>
        </div>
      )}
    </article>
  )
}


export default function ExchangeEnvelopes({
  loadTransactions = fetchGoldTransactions,
  reportEnvelope = reportEnvelopeToRiss,
  claimEnvelope = claimReportedEnvelope,
  canClaimEnvelopes = true,
} = {}) {
  const [txs, setTxs] = useState(null)
  const [error, setError] = useState('')
  const [reportErrors, setReportErrors] = useState({})
  const [claiming, setClaiming] = useState(null)
  const claimingRef = useRef(false)
  const [now, setNow] = useState(() => new Date())
  const [reporting, setReporting] = useState(null)
  const reportingRef = useRef(new Set())
  const [reportPinTarget, setReportPinTarget] = useState(null)
  const reportPinTargetRef = useRef(null)
  const reportBlockedRef = useRef(new Set())
  const [reportBlocked, setReportBlocked] = useState(() => new Set())
  const [showArchive, setShowArchive] = useState(false)
  const [showCancelled, setShowCancelled] = useState(false)
  // Cancel-envelope flow: pick reason → operator PIN → void the sale.
  const [cancelTarget, setCancelTarget] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelPinOpen, setCancelPinOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    let alive = true
    Promise.resolve().then(() => loadTransactions())
      .then((rows) => { if (alive) setTxs(rows) })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load envelopes') })
    return () => { alive = false }
  }, [loadTransactions])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const { awaiting, ready, holding, archived, cancelled } = useMemo(() => {
    const live = (txs || []).filter((t) => !isCancelledTx(t))
    const active = live.filter((t) => !t.claimedAt)
    const reported = active.filter((t) => !isAwaitingReport(t))
    return {
      awaiting: active.filter(isAwaitingReport).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)),
      ready: reported.filter((t) => isEnvelopeReady(t, now)).sort((a, b) => a.holdUntil - b.holdUntil),
      holding: reported.filter((t) => !isEnvelopeReady(t, now)).sort((a, b) => a.holdUntil - b.holdUntil),
      archived: live.filter((t) => t.claimedAt).sort((a, b) => b.claimedAt - a.claimedAt),
      cancelled: (txs || []).filter(isCancelledTx).sort((a, b) => (b.cancelledAt || 0) - (a.cancelledAt || 0)),
    }
  }, [txs, now])

  const requestReportAuthorization = (tx) => {
    if (reportPinTargetRef.current || reportingRef.current.size || reportBlockedRef.current.has(tx.id) || !canReport(tx) ||
        cancelTarget || cancelling || claimingRef.current || tx.claimedAt || isCancelledTx(tx)) return
    reportPinTargetRef.current = tx
    setReportPinTarget(tx)
  }

  const cancelReportAuthorization = () => {
    if (reportingRef.current.size) return
    reportPinTargetRef.current = null
    setReportPinTarget(null)
  }

  const sendReport = async (tx, managerPin) => {
    if (reportingRef.current.size || reportBlockedRef.current.has(tx.id) || !canReport(tx) ||
        reportPinTargetRef.current?.id !== tx.id || cancelTarget || cancelling || claimingRef.current || tx.claimedAt || isCancelledTx(tx)) return
    reportingRef.current.add(tx.id)
    setReporting(tx.id)
    setError('')
    setReportErrors((previous) => ({ ...previous, [tx.id]: null }))
    // Each explicit attempt creates fresh feedback, even if the same validation
    // message repeats. Other envelopes keep their own unresolved errors.
    const showReportError = message => setReportErrors((previous) => ({ ...previous, [tx.id]: { message } }))
    try {
      // The typed PIN is used by this request only. The server validates the
      // manager and builds the report from the saved purchase, not an editable
      // payload, receipt or client-generated hold timestamp.
      const result = await reportEnvelope({ transactionId: tx.id })
      const delivery = result?.reportDelivery
      if (result?.transactionId !== tx.id || result.environment !== 'production' || result.accepted !== false ||
          !['uploaded', 'sending', 'needs_review', 'failed'].includes(result.status) ||
          delivery?.environment !== 'production' || delivery.accepted !== false || delivery.state !== result.status) {
        throw new Error('The server response did not verify report delivery. Refresh the envelopes before retrying.')
      }
      let patch = { reportDelivery: delivery }
      if (result.status === 'uploaded') {
        patch = {
          ...patch, reportStatus: result.reportStatus,
          reportedAt: toDate(result.reportedAt), holdStartedAt: toDate(result.holdStartedAt), holdUntil: toDate(result.holdUntil),
          reportConfirmation: result.reportConfirmation,
          reportReference: result.reportConfirmation?.reference,
          reportConfirmedBy: result.reportConfirmation?.confirmedBy,
        }
        if (result.reportConfirmation?.method !== 'cloud_delivery' || isAwaitingReport(patch) ||
            delivery.requestId !== result.reportConfirmation.reference ||
            delivery.fileCount !== result.reportConfirmation.fileCount || delivery.uploadedFiles !== result.reportConfirmation.uploadedFiles) {
          throw new Error('The server response did not verify report delivery. Refresh the envelopes before retrying.')
        }
      }
      // Non-uploaded outcomes update delivery only, never the hold lifecycle.
      setTxs((rows) => rows.map((row) => row.id === tx.id ? { ...row, ...patch } : row))
      setNow(new Date())
      if (result.status === 'failed') showReportError(delivery.retryable === true
        ? 'The report was not sent. Correct the problem and click Report to start countdown to retry.'
        : 'The report could not be sent. Contact an administrator before retrying.')
      else if (result.status === 'needs_review') showReportError('Report delivery needs review. Do not send this envelope again until its saved delivery record has been checked. The hold has not started here.')
    } catch (failure) {
      const code = String(failure?.code || '').replace(/^functions\//, '')
      const preflight = ['unauthenticated', 'permission-denied', 'invalid-argument', 'failed-precondition', 'resource-exhausted'].includes(code)
      if (!preflight) {
        // A timeout or malformed receipt is not proof that nothing was sent.
        // This local guard disappears only after a fresh server load.
        reportBlockedRef.current.add(tx.id)
        setReportBlocked(new Set(reportBlockedRef.current))
      }
      showReportError(preflight
        ? ['permission-denied', 'unauthenticated'].includes(code)
          ? 'You do not have permission to report this envelope. Sign in and use Moses’s or Cinara’s manager PIN.'
          : envelopeActionError(failure, 'The report could not be sent. Correct the problem before retrying.')
        : 'Report delivery could not be verified. The hold has not started here. Refresh or contact an administrator before sending again.')
    } finally {
      reportingRef.current.delete(tx.id)
      setReporting(null)
      reportPinTargetRef.current = null
      setReportPinTarget(null)
    }
  }

  const claim = async (tx) => {
    if (!canClaimEnvelopes || claimingRef.current || reportPinTargetRef.current || reportingRef.current.size || reportBlockedRef.current.has(tx.id) ||
        deliveryLocked(tx) || cancelling || cancelTarget || !isEnvelopeReady(tx, new Date())) return
    claimingRef.current = true
    setClaiming(tx.id)
    setError('')
    try {
      const result = await claimEnvelope({ transactionId: tx.id })
      const claimedAt = toDate(result?.claimedAt)
      if (result?.transactionId !== tx.id || !claimedAt) {
        throw new Error('The server did not return a valid claim receipt. Refresh the envelopes before retrying.')
      }
      setTxs((rows) => rows.map((row) => row.id === tx.id
        ? { ...row, claimedAt, claimedBy: result.claimedBy || null } : row))
    } catch (e) {
      setError(envelopeActionError(e, 'Failed to claim envelope. Refresh before retrying.'))
    } finally {
      claimingRef.current = false
      setClaiming(null)
    }
  }

  const startCancel = (tx) => {
    if (reportPinTargetRef.current || reportingRef.current.size || reportBlockedRef.current.has(tx.id) || deliveryLocked(tx) ||
        cancelling || claimingRef.current || tx.claimedAt || isCancelledTx(tx)) return
    setError('')
    setCancelReason('')
    setCancelTarget(tx)
  }

  // PIN verified → void the sale. The record stays (legal retention); status
  // flips to 'cancelled' and the envelope leaves the active lists.
  const performCancel = async (operator) => {
    const tx = cancelTarget
    setCancelPinOpen(false)
    setCancelTarget(null)
    if (!tx || cancelling || reportingRef.current.has(tx.id) || reportBlockedRef.current.has(tx.id) || deliveryLocked(tx)) return
    setCancelling(true)
    setError('')
    try {
      await runTransaction(db, async (t) => {
        const ref = doc(db, 'gold_transactions', tx.id)
        const snap = await t.get(ref)
        if (!snap.exists()) throw new Error('Transaction not found')
        const current = snap.data()
        if (current.status === 'cancelled') throw new Error('This envelope is already cancelled.')
        if (current.envelopeClaimedAt) throw new Error('This envelope was already claimed — it cannot be cancelled.')
        if (deliveryLocked(current)) throw new Error('Report delivery is in progress or needs review. Refresh before cancelling this envelope.')
        t.update(ref, {
          status: 'cancelled',
          cancelledAt: serverTimestamp(),
          cancelledBy: operator,
          cancelReason: cancelReason.trim() || 'Seller withdrew from the sale',
          updatedAt: serverTimestamp(),
        })
      })
      logGoldEvent('envelope_cancelled', {
        transactionNumber: tx.transactionNumber,
        envelopeNumber: tx.envelopeNumber,
        payout: tx.payout,
        weightGrams: tx.weightGrams,
        operator: operator?.name || null,
        reason: cancelReason.trim() || 'Seller withdrew from the sale',
      })
      setTxs((rows) => rows.map((r) => (
        r.id === tx.id
          ? { ...r, status: 'cancelled', cancelledAt: new Date(), cancelledBy: operator?.name || null, cancelReason: cancelReason.trim() || 'Seller withdrew from the sale' }
          : r
      )))
    } catch (e) {
      setError(e?.message || 'Failed to cancel envelope')
    } finally {
      setCancelling(false)
    }
  }

  if (error && !txs) {
    return <div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div>
  }
  if (!txs) {
    return <div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading envelopes…</div>
  }

  return (
    <div className="space-y-5">
      {error && <div role="alert" className="rounded-lg bg-destructive/10 border border-destructive/25 p-3 text-sm text-destructive">{error}</div>}

      {/* New purchases need manual reporting. Owner-attested historical email
          reports retain their separate source and purchase-date hold. */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground mb-2 flex items-center gap-2">
          <Icon name="FileCheck" size={16} /> To report ({awaiting.length})
        </h2>
        {awaiting.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4">
            No envelopes awaiting production reporting.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Manager review required. Check the purchase before reporting.</p>
            {awaiting.map((t) => <EnvelopeRow key={t.id} tx={t} now={now} onReport={requestReportAuthorization} reporting={reporting} reportBlocked={reportBlocked.has(t.id)} reportError={reportErrors[t.id]} actionsDisabled={Boolean(reportPinTarget || reporting || cancelling || cancelTarget || claiming)} onCancelEnvelope={startCancel} />)}
          </div>
        )}
      </section>

      {/* Ready envelopes appear before those still serving their hold. */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-success mb-2 flex items-center gap-2">
          <Icon name="PackageCheck" size={16} /> Ready to use ({ready.length})
        </h2>
        {ready.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4">
            Nothing ready yet — envelopes appear here after reporting and the {HOLD_DAYS}-day hold.
          </p>
        ) : (
          <div className="space-y-3">
            {ready.map((t) => <EnvelopeRow key={t.id} tx={t} now={now} onClaim={claim} claiming={claiming} canClaimEnvelopes={canClaimEnvelopes} reportBlocked={reportBlocked.has(t.id)} actionsDisabled={Boolean(reportPinTarget || reporting || cancelling || cancelTarget)} onCancelEnvelope={startCancel} />)}
          </div>
        )}
      </section>

      {/* On hold */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
          <Icon name="Clock" size={16} /> On {HOLD_DAYS}-day hold ({holding.length})
        </h2>
        {holding.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4">No envelopes on hold.</p>
        ) : (
          <div className="space-y-3">
            {holding.map((t) => <EnvelopeRow key={t.id} tx={t} now={now} onClaim={claim} claiming={claiming} reportBlocked={reportBlocked.has(t.id)} actionsDisabled={Boolean(reportPinTarget || reporting || cancelling || cancelTarget)} onCancelEnvelope={startCancel} />)}
          </div>
        )}
      </section>

      {/* Archive (claimed) — kept out of the way to stay clean */}
      <section>
        <button
          type="button"
          onClick={() => setShowArchive((v) => !v)}
          aria-expanded={showArchive}
          className="min-h-11 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name={showArchive ? 'ChevronDown' : 'ChevronRight'} size={16} />
          Claimed archive ({archived.length})
        </button>
        {showArchive && (
          <div className="mt-3 space-y-2">
            {archived.length === 0 ? (
              <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4">Nothing claimed yet.</p>
            ) : (
              archived.map((t) => (
                <div key={t.id} className="rounded-xl border border-border bg-muted/20 p-3 flex flex-wrap items-center gap-3 text-sm">
                  <Icon name="Archive" size={16} className="text-muted-foreground flex-shrink-0" />
                  <span className="font-medium text-foreground">Envelope {t.envelopeNumber || '—'}</span>
                  <span className="text-muted-foreground truncate">
                    {t.transactionNumber} · {t.customerName || 'Customer'} · {grams(t.weightGrams)} · {usd(t.payout)}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                    Claimed {fmtDate(t.claimedAt)}{t.claimedBy ? ` · ${t.claimedBy}` : ''}
                  </span>
                  <LegacyPoliceReportDetails tx={t} className="basis-full" />
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Cancelled — kept for legal retention, out of the active flow */}
      <section>
        <button
          type="button"
          onClick={() => setShowCancelled((v) => !v)}
          aria-expanded={showCancelled}
          className="min-h-11 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name={showCancelled ? 'ChevronDown' : 'ChevronRight'} size={16} />
          Cancelled ({cancelled.length})
        </button>
        {showCancelled && (
          <div className="mt-3 space-y-2">
            {cancelled.length === 0 ? (
              <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4">No cancelled envelopes.</p>
            ) : (
              cancelled.map((t) => (
                <div key={t.id} className="rounded-xl border border-destructive/25 bg-destructive/5 p-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <Icon name="Ban" size={16} className="text-destructive flex-shrink-0" />
                  <span className="font-medium text-foreground">Envelope {t.envelopeNumber || '—'}</span>
                  <span className="text-muted-foreground truncate">
                    {t.transactionNumber} · {t.customerName || 'Customer'} · {grams(t.weightGrams)} · {usd(t.payout)}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                    Cancelled {fmtDate(t.cancelledAt)}{t.cancelledBy ? ` · ${t.cancelledBy}` : ''}{t.cancelReason ? ` · ${t.cancelReason}` : ''}
                  </span>
                  <LegacyPoliceReportDetails tx={t} className="basis-full" />
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {reportPinTarget && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Confirm report">
          <div className="max-w-sm rounded-xl bg-background p-6"><h2 className="text-lg font-semibold">Report purchase {reportPinTarget.envelopeNumber || reportPinTarget.transactionNumber}?</h2>
            <p className="my-4">The report will be attributed to your signed-in manager account.</p>
            <Button variant="outline" onClick={cancelReportAuthorization}>Cancel</Button>
            <Button onClick={() => sendReport(reportPinTarget)}>Confirm report</Button>
          </div>
        </div>
      )}

      {/* Cancel step 1: confirm + reason */}
      {cancelTarget && !cancelPinOpen && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Cancel envelope">
          <div className="w-full max-w-sm bg-background rounded-2xl border border-border/60 shadow-elevation-4 p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="Ban" size={20} className="text-destructive" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-foreground">Cancel envelope {cancelTarget.envelopeNumber || '—'}?</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {cancelTarget.transactionNumber} · {cancelTarget.customerName || 'Customer'} · {usd(cancelTarget.payout)}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              The sale is voided and leaves the envelope lists. The record and photos are kept for legal retention.
            </p>
            {!isAwaitingReport(cancelTarget) && (
              <p className="text-xs text-foreground mb-3">
                {cancelTarget.reportStatus === 'legacy_reported'
                  ? 'Cancelling here does not withdraw the report emailed to local police.'
                  : 'Cancelling here does not withdraw a report already sent to RISS.'}
              </p>
            )}
            <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="cancel-reason">Reason</label>
            <input
              id="cancel-reason"
              type="text"
              maxLength={1000}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Seller withdrew from the sale"
              className="w-full h-11 px-3 border border-border/60 rounded-lg text-base bg-background/80 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary mb-4"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setCancelTarget(null)}>
                Keep envelope
              </Button>
              <Button variant="destructive" className="flex-1" onClick={() => setCancelPinOpen(true)} iconName="Ban" iconPosition="left">
                Cancel sale
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel step 2: operator PIN (identifies who voided it) */}
      {cancelTarget && cancelPinOpen && (
        <OperatorPinDialog
          title="Confirm Cancellation"
          subtitle={`Envelope ${cancelTarget.envelopeNumber || '—'} · ${usd(cancelTarget.payout)} — enter your PIN`}
          submitLabel="Cancel Sale"
          onVerified={performCancel}
          onCancel={() => { setCancelPinOpen(false); setCancelTarget(null) }}
        />
      )}
    </div>
  )
}
